import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const MODES = ['OFF', 'SHADOW', 'AUTO'];
const PERSONAS = ['doradca-kumpel', 'profesjonalny', 'luzny'];

export default function AgentPage() {
  const [status, setStatus] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [editingDraft, setEditingDraft] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, r, sg] = await Promise.all([
        api.get('/agent/status'),
        api.get('/agent/drafts', { params: { status: filterStatus, limit: 50 } }),
        api.get('/agent/report/today').catch(() => null),
        api.get('/agent/suggestions').catch(() => null),
      ]);
      setStatus(s.data);
      setDrafts(d.data.items || []);
      if (r) setReport(r.data);
      if (sg) setSuggestions(sg.data?.suggestions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd ładowania');
    } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const setMode = async (mode) => {
    try {
      await api.put('/agent/state', { mode });
      toast.success(`Tryb: ${mode}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const updateField = async (patch) => {
    try {
      await api.put('/agent/state', patch);
      toast.success('Zapisano');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const triggerTick = async (mode) => {
    setRunning(true);
    try {
      const r = await api.post('/agent/tick', null, { params: mode ? { mode } : {} });
      toast.success(`Tick: gen=${r.data.generated || 0}, drafts=${r.data.drafts || 0}, sent=${r.data.auto_sent || 0}, skip=${r.data.skipped || 0}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd ticka');
    } finally { setRunning(false); }
  };

  const approveDraft = async (id, edit = null) => {
    try {
      await api.post(`/agent/drafts/${id}/approve`, edit);
      toast.success('Wysłane');
      setEditingDraft(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const rejectDraft = async (id) => {
    try {
      await api.post(`/agent/drafts/${id}/reject`);
      toast.success('Odrzucone');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const startEdit = (d) => {
    setEditingDraft(d.id);
    setEditBody(d.body || '');
    setEditSubject(d.subject || '');
  };

  const saveEdit = async (id) => {
    await approveDraft(id, { body: editBody, subject: editSubject });
  };

  const state = status?.state || {};
  const stats = status?.drafts || { pending: 0, sent: 0, rejected: 0 };

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Autonomiczny Agent</h1>
          <p className="text-sm text-muted-foreground">Marketing AI · wybiera kogo, kiedy, czym i jak</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>Odśwież</Button>
          <Button onClick={() => triggerTick('SHADOW')} disabled={running} variant="outline">Test tick (SHADOW)</Button>
          <Button onClick={() => triggerTick()} disabled={running || state.mode === 'OFF'}>Wymuś tick</Button>
        </div>
      </div>

      {loading && !status ? (
        <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-40" /></div>
      ) : (
        <>
          {/* Tryby */}
          <Card>
            <CardContent className="p-4">
              <Label className="mb-2 block">Tryb pracy</Label>
              <div className="flex gap-2 mb-3">
                {MODES.map(m => (
                  <Button
                    key={m}
                    variant={state.mode === m ? 'default' : 'outline'}
                    onClick={() => setMode(m)}
                  >
                    {m}
                    {m === 'OFF' && state.mode === m && ' (idle)'}
                    {m === 'SHADOW' && state.mode === m && ' (tworzy drafty)'}
                    {m === 'AUTO' && state.mode === m && ' (wysyła sam)'}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                <b>OFF:</b> agent śpi. <b>SHADOW:</b> generuje drafty, admin akceptuje. <b>AUTO:</b> wysyła samodzielnie (uważnie!).
              </p>
            </CardContent>
          </Card>

          {/* Statystyki + state */}
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Tick count" value={state.tick_count || 0} />
            <Stat label="Drafty czekają" value={stats.pending} highlight={stats.pending > 0} />
            <Stat label="Wysłane (z draftów)" value={stats.sent} />
            <Stat label="Odrzucone" value={stats.rejected} />
          </div>

          {/* Sugestie (S6) */}
          {suggestions.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <Label className="mb-2 block">💡 Sugestie systemu</Label>
                <div className="space-y-2">
                  {suggestions.map((s, i) => (
                    <div key={i} className={`p-2 rounded text-sm border ${
                      s.level === 'critical' ? 'bg-destructive/10 border-destructive/30' :
                      s.level === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      'bg-blue-500/10 border-blue-500/30'
                    }`}>
                      <Badge variant="outline" className="text-[10px] mr-2">{s.category}</Badge>
                      {s.message}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Raport dzienny (S6) */}
          {report && (
            <Card>
              <CardContent className="p-4">
                <Label className="mb-2 block">📊 Raport (ostatnie 24h)</Label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
                  <Stat label="Drafty" value={report.drafts?.total || 0} />
                  <Stat label="Wysłane SMS" value={report.outgoing?.sms_all || 0} />
                  <Stat label="Wysłane email" value={report.outgoing?.email || 0} />
                  <Stat label="Konwersje" value={report.conversions?.count || 0} highlight={report.conversions?.count > 0} />
                  <Stat label="Revenue" value={`${report.conversions?.revenue || 0} zł`} />
                  <Stat label="Opt-outs" value={report.new_opt_outs || 0} />
                </div>
                {report.intent_breakdown && Object.keys(report.intent_breakdown).length > 0 && (
                  <div className="mt-3">
                    <Label className="text-xs">Intent breakdown (incoming)</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Object.entries(report.intent_breakdown).map(([k, v]) => (
                        <Badge key={k} variant="outline">{k}: {v}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {report.top_offers?.length > 0 && (
                  <div className="mt-3">
                    <Label className="text-xs">Top oferty (drafty)</Label>
                    <div className="space-y-1 mt-1">
                      {report.top_offers.map((o, i) => (
                        <div key={i} className="text-xs flex justify-between p-1 rounded bg-muted/30">
                          <span className="truncate">{o.name}</span>
                          <span className="font-mono">{o.sent}/{o.drafts}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          <p className="text-xs text-muted-foreground">
            Ostatni tick: {state.last_tick_at ? state.last_tick_at.replace('T', ' ').slice(0, 19) : '–'}
            {state.last_tick_result && ` · gen=${state.last_tick_result.generated || 0} drafts=${state.last_tick_result.drafts || 0} sent=${state.last_tick_result.auto_sent || 0}`}
          </p>

          {/* Settings */}
          <Card>
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label>Persona agenta</Label>
                <select value={state.persona || 'doradca-kumpel'} onChange={(e) => updateField({ persona: e.target.value })} className="w-full mt-1 p-2 rounded border border-border bg-background text-sm">
                  {PERSONAS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label>Imię agenta (w wiadomościach)</Label>
                <Input value={state.persona_name || ''} onChange={(e) => updateField({ persona_name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Max wiadomości / tick</Label>
                <Input type="number" value={state.max_per_tick || 20} onChange={(e) => updateField({ max_per_tick: parseInt(e.target.value) || 20 })} className="mt-1" />
              </div>
              <div>
                <Label>Interwał (min)</Label>
                <Input type="number" value={state.tick_interval_minutes || 10} onChange={(e) => updateField({ tick_interval_minutes: parseInt(e.target.value) || 10 })} className="mt-1" />
              </div>
              <div>
                <Label>Globalny dzienny cap</Label>
                <Input type="number" value={state.global_daily_cap || 500} onChange={(e) => updateField({ global_daily_cap: parseInt(e.target.value) || 500 })} className="mt-1" />
              </div>
              <div>
                <Label>Cooldown per kontakt (dni)</Label>
                <Input type="number" value={state.default_cooldown_days || 7} onChange={(e) => updateField({ default_cooldown_days: parseInt(e.target.value) || 7 })} className="mt-1" />
              </div>
            </CardContent>
          </Card>

          {/* Drafty */}
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-3">
                <Label>Drafty</Label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="p-1 rounded border border-border bg-background text-sm">
                  <option value="pending">pending</option>
                  <option value="sent">sent</option>
                  <option value="rejected">rejected</option>
                  <option value="all">all</option>
                </select>
              </div>

              {drafts.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">
                  Brak draftów dla "{filterStatus}". Włącz tryb SHADOW i kliknij "Test tick" żeby wygenerować pierwsze.
                </div>
              ) : (
                <div className="space-y-2">
                  {drafts.map(d => (
                    <div key={d.id} className="border border-border/40 rounded p-3 text-sm">
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline">{d.channel}</Badge>
                            <Badge variant={d.status === 'pending' ? 'default' : d.status === 'sent' ? 'outline' : 'destructive'}>{d.status}</Badge>
                            <span className="font-medium">{d.first_name} {d.last_name}</span>
                            <span className="text-xs text-muted-foreground">{d.phone || d.email}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Oferta: <b>{d.offer_name}</b> · {d.created_at?.replace('T', ' ').slice(0, 19)}
                          </div>
                        </div>
                      </div>

                      {editingDraft === d.id ? (
                        <div className="space-y-2">
                          {d.channel === 'email' && (
                            <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} placeholder="temat" />
                          )}
                          <textarea
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            className="w-full min-h-24 p-2 rounded border border-border bg-background text-sm"
                          />
                          <div className="flex gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => setEditingDraft(null)}>Anuluj</Button>
                            <Button size="sm" onClick={() => saveEdit(d.id)}>Wyślij (po edycji)</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {d.subject && <div className="text-xs font-medium mb-1">Temat: {d.subject}</div>}
                          <div className="text-sm whitespace-pre-wrap bg-muted/30 p-2 rounded">{d.body}</div>
                          {d.status === 'pending' && (
                            <div className="flex gap-2 mt-2 justify-end">
                              <Button variant="outline" size="sm" onClick={() => rejectDraft(d.id)}>Odrzuć</Button>
                              <Button variant="outline" size="sm" onClick={() => startEdit(d)}>Edytuj</Button>
                              <Button size="sm" onClick={() => approveDraft(d.id)}>Akceptuj (wyślij)</Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className={`p-3 rounded-lg border border-border/40 ${highlight ? 'bg-destructive/10 border-destructive/30' : 'bg-card'}`}>
    <div className="text-2xl font-mono font-semibold">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);
